// FILE: src/components/QuestionFactory/caseFactoryUtils.js
// Pure helpers for the Generate Case Study flow (unit-tested).

/** Ordered step plan derived from the generation inputs. */
export function stepPlan(inputs) {
  const stages = Math.max(1, Number(inputs.stages) || 1);
  const plan = [{ key: 'skeleton', label: 'Case skeleton', invoke: { step: 'skeleton' } }];
  for (let s = 1; s <= stages; s += 1) {
    plan.push({ key: `artifacts-${s}`, label: `Stage ${s} artifacts`, invoke: { step: 'artifacts', stage: s } });
  }
  for (let s = 1; s <= stages; s += 1) {
    plan.push({ key: `questions-${s}`, label: `Stage ${s} questions`, invoke: { step: 'questions', stage: s } });
  }
  plan.push({ key: 'validate', label: 'Validate & review', invoke: { step: 'validate' } });
  return plan;
}

/** Which plan entries are done, and the next one to run (null when complete). */
export function planProgress(plan, stepStatus = {}) {
  const doneKeys = new Set();
  if (stepStatus.skeleton === 'done') doneKeys.add('skeleton');
  Object.entries(stepStatus.artifacts || {}).forEach(([s, v]) => { if (v === 'done') doneKeys.add(`artifacts-${s}`); });
  Object.entries(stepStatus.questions || {}).forEach(([s, v]) => { if (v === 'done') doneKeys.add(`questions-${s}`); });
  if (stepStatus.validate === 'done') doneKeys.add('validate');
  const next = plan.find((p) => !doneKeys.has(p.key)) || null;
  return { doneKeys, next, doneCount: doneKeys.size, total: plan.length };
}

/** Learner-panel-shaped stages for previewing a bundle with CasePanel. */
export function bundleToPanelStages(bundle) {
  return ((bundle && bundle.stages) || []).map((st) => ({
    sequence: st.sequence, title: st.title, intro: st.intro,
    sections: (st.sections || []).map((sec, i) => ({ id: `sec-${st.sequence}-${i}`, ...sec })),
    artifacts: (st.artifacts || []).map((a) => ({ id: a.ref, ...a })),
  }));
}

/** Compact bundle summary for the draft header. */
export function bundleSummary(bundle) {
  const stages = (bundle && bundle.stages) || [];
  return {
    title: bundle && bundle.case ? bundle.case.title : null,
    slug: bundle && bundle.case ? bundle.case.slug : null,
    stages: stages.length,
    sections: stages.reduce((n, s) => n + ((s.sections || []).length), 0),
    artifacts: stages.reduce((n, s) => n + ((s.artifacts || []).length), 0),
    questions: stages.reduce((n, s) => n + ((s.questions || []).length), 0),
  };
}

const snip = (text, max = 48) => {
  const t = String(text || '').replace(/\s+/g, ' ').trim();
  return t.length <= max ? t : `${t.slice(0, max - 1)}…`;
};

/**
 * Descriptive regenerate-scope options for a bundle. Each option carries
 * enough context (titles, types, stage, domain, stem snippet) to match a
 * reviewer finding to the element it lives in without opening the preview.
 * Shape: { v: "kind:value", label } — v is the regen-scope encoding the
 * panel already uses (stage:N | artifact:ref | question:caseSequence).
 */
export function regenOptionsForBundle(bundle) {
  const stages = (bundle && bundle.stages) || [];
  const opts = [];
  stages.forEach((st) => {
    opts.push({
      v: `stage:${st.sequence}`,
      label: `Stage ${st.sequence} — ${st.title || 'untitled'} (narrative + artifacts + questions)`,
    });
  });
  stages.forEach((st) => {
    (st.artifacts || []).forEach((a) => {
      opts.push({
        v: `artifact:${a.ref}`,
        label: `Artifact ${a.ref} — ${a.title || 'untitled'} (${a.artifact_type || 'artifact'}, stage ${st.sequence})`,
      });
    });
  });
  stages.forEach((st) => {
    (st.questions || []).forEach((q) => {
      const qp = q.question || {};
      const domain = (qp.metadata && qp.metadata.performanceDomain) || null;
      const bits = [qp.type || 'question', domain, `stage ${st.sequence}`].filter(Boolean).join(' · ');
      opts.push({
        v: `question:${q.case_sequence}`,
        label: `Q${q.case_sequence} — ${bits} — “${snip(qp.question)}”`,
      });
    });
  });
  return opts;
}

// Free-text fallbacks for review issues generated before the review prompt
// emitted structured targets: pull artifact refs, question ids / numbers,
// and stage numbers out of the `where` + `detail` strings.
function parseTargetsFromText(text, bundle) {
  const stages = (bundle && bundle.stages) || [];
  const found = [];
  const push = (v) => { if (!found.includes(v)) found.push(v); };

  const refs = new Set(stages.flatMap((st) => (st.artifacts || []).map((a) => String(a.ref).toLowerCase())));
  (String(text).match(/\bs\d+-a\d+\b/gi) || []).forEach((m) => {
    if (refs.has(m.toLowerCase())) push(`artifact:${m.toLowerCase()}`);
  });

  stages.forEach((st) => (st.questions || []).forEach((q) => {
    const id = String((q.question || {}).id || '');
    if (id && String(text).includes(id)) push(`question:${q.case_sequence}`);
  }));
  const seqs = new Set(stages.flatMap((st) => (st.questions || []).map((q) => Number(q.case_sequence))));
  [...String(text).matchAll(/\b(?:question|q)\s*#?\s*(\d+)\b/gi)].forEach((m) => {
    if (seqs.has(Number(m[1]))) push(`question:${Number(m[1])}`);
  });

  const stageSeqs = new Set(stages.map((st) => Number(st.sequence)));
  [...String(text).matchAll(/\bstage\s+(\d+)\b/gi)].forEach((m) => {
    if (stageSeqs.has(Number(m[1]))) push(`stage:${Number(m[1])}`);
  });
  return found;
}

/**
 * Resolve a consistency-review issue to the regen-scope options it points at,
 * most specific first (artifact/question before stage). Prefers the issue's
 * structured `targets` array (new review shape); falls back to parsing the
 * free-text `where` + `detail` for older drafts. Only targets that actually
 * exist in the bundle are returned.
 */
export function issueTargets(issue, bundle) {
  const options = regenOptionsForBundle(bundle);
  const byV = new Map(options.map((o) => [o.v, o]));
  const vs = [];
  const push = (v) => { if (v && byV.has(v) && !vs.includes(v)) vs.push(v); };

  (Array.isArray(issue && issue.targets) ? issue.targets : []).forEach((t) => {
    if (!t || !t.kind) return;
    if (t.kind === 'artifact') push(`artifact:${String(t.ref || '').toLowerCase()}`);
    else if (t.kind === 'question') push(`question:${Number(t.case_sequence)}`);
    else if (t.kind === 'stage') push(`stage:${Number(t.stage)}`);
  });
  if (vs.length === 0 && issue) {
    parseTargetsFromText(`${issue.where || ''} ${issue.detail || ''}`, bundle).forEach(push);
  }
  // Specific elements first; whole-stage rewrites are the blunt instrument.
  const rank = (v) => (v.startsWith('stage:') ? 1 : 0);
  vs.sort((a, b) => rank(a) - rank(b));
  return vs.map((v) => byV.get(v));
}

/** Prefilled regeneration guidance derived from a reviewer finding. */
export function guidanceForIssue(issue) {
  if (!issue) return '';
  const parts = [
    `Reviewer finding (${issue.severity || 'unrated'}) at ${issue.where || 'unspecified location'}: ${issue.detail || ''}`.trim(),
  ];
  if (issue.suggested_fix) parts.push(`Suggested fix: ${issue.suggested_fix}`);
  parts.push('Resolve this finding while keeping everything else consistent with the rest of the case.');
  return parts.join(' ');
}

/** Decode a "kind:value" regen-scope string into the edge-function scope object. */
export function scopeFromV(v) {
  const [kind, val] = String(v || '').split(':');
  if (kind === 'artifact') return { kind, ref: val };
  if (kind === 'question') return { kind, case_sequence: Number(val) };
  if (kind === 'stage') return { kind, stage: Number(val) };
  return null;
}

/**
 * Plan the automatic post-validate fix pass (2026-07-27, Bryan: reviews are
 * faster when high/medium findings regenerate themselves): every artifact or
 * question targeted by a high/medium consistency issue, deduped per element
 * with the guidance of ALL its findings merged. Stage-kind targets are
 * excluded on purpose — a whole-stage rewrite (narrative + artifacts +
 * questions) is too blunt to run unattended and stays a manual decision.
 * Returns [{ v, label, scope, guidance }]; the caller runs ONE pass and
 * re-validates once — leftovers surface for manual review.
 */
export function autoFixPlan(review, bundle) {
  const issues = (review && Array.isArray(review.issues)) ? review.issues : [];
  const chosen = new Map(); // v -> { option, guidances }
  issues
    .filter((iss) => iss && (iss.severity === 'high' || iss.severity === 'medium'))
    .forEach((iss) => {
      issueTargets(iss, bundle)
        .filter((o) => !o.v.startsWith('stage:'))
        .forEach((o) => {
          if (!chosen.has(o.v)) chosen.set(o.v, { option: o, guidances: [] });
          const g = guidanceForIssue(iss);
          if (!chosen.get(o.v).guidances.includes(g)) chosen.get(o.v).guidances.push(g);
        });
    });
  return [...chosen.values()].map(({ option, guidances }) => ({
    v: option.v,
    label: option.label,
    scope: scopeFromV(option.v),
    guidance: guidances.join('\n'),
  }));
}
